import { conciergeNoteOf, customerRequestWords } from "@lafayette/domain";
import type {
  StaffFulfilmentViewResponse,
  StaffRequestDetail,
  StaffRequestPriorityValue,
  StaffRequestQuestion,
  StaffRequestStateValue,
  StaffRequestWorkStatusValue,
} from "@lafayette/schemas";

// The detail answer carries the request, the notes and the history as open records (the API returns every column to an authorized
// staff member). This file reads them into typed models with a guard for each key, so one missing or retyped column shows as an empty
// value and never crashes the page. It also builds the ONE model of "what the customer sees": a closed list of keys chosen from the
// detail, so a staff-only field cannot reach that block by accident (the sentinel test proves it).

type RecordLike = Readonly<Record<string, unknown>>;

const text = (value: unknown): string | null =>
  typeof value === "string" && value !== "" ? value : null;
const whole = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

export type RequestFacts = Readonly<{
  id: string;
  state: StaffRequestStateValue;
  version: number;
  title: string;
  requestType: string;
  priority: StaffRequestPriorityValue;
  /** The owner id kept on the record, also after the request closed. */
  ownerStaffUserId: string | null;
  workStatus: StaffRequestWorkStatusValue;
  partySize: number | null;
  dateFrom: string | null;
  dateTo: string | null;
  preferredTime: string | null;
  timeZone: string | null;
  locationText: string | null;
  specialRequests: string | null;
  budget: string | null;
  details: readonly (readonly [string, string])[];
  customerStatusSummary: string | null;
  pendingCustomerQuestion: string | null;
  confirmationEvidenceKind: string | null;
  confirmationEvidenceReference: string | null;
  completionEvidenceKind: string | null;
  source: string | null;
  sourceMessageRef: string | null;
  createdAt: string | null;
  submittedAt: string | null;
  stateChangedAt: string | null;
}>;

const PRIORITIES: readonly string[] = ["STANDARD", "HIGH", "URGENT"];
const WORK_STATUSES: readonly string[] = ["NONE", "WITH_SUPPLIER", "INTERNAL_REVIEW"];

function budgetText(value: unknown): string | null {
  if (typeof value !== "object" || value === null) return null;
  const budget = value as RecordLike;
  const amount = whole(budget.amountMinor);
  const currency = text(budget.currency);
  if (amount === null || currency === null) return null;
  return `${(amount / 100).toFixed(2)} ${currency} (indicative, not a payment)`;
}

function detailsOf(value: unknown): readonly (readonly [string, string])[] {
  if (typeof value !== "object" || value === null) return [];
  return Object.entries(value as RecordLike)
    .filter(([, entry]) => ["string", "number", "boolean"].includes(typeof entry))
    .map(([key, entry]) => [key, String(entry)] as const);
}

export function readRequestFacts(detail: StaffRequestDetail): RequestFacts {
  const r = detail.request as RecordLike;
  const priority = text(r.priority);
  const workStatus = text(r.workStatus);
  return {
    id: text(r.id) ?? "",
    state: (text(r.state) ?? "SUBMITTED") as StaffRequestStateValue,
    version: whole(r.version) ?? 1,
    title: text(r.title) ?? "Untitled request",
    requestType: text(r.requestType) ?? "OTHER",
    priority: (priority !== null && PRIORITIES.includes(priority)
      ? priority
      : "STANDARD") as StaffRequestPriorityValue,
    workStatus: (workStatus !== null && WORK_STATUSES.includes(workStatus)
      ? workStatus
      : "NONE") as StaffRequestWorkStatusValue,
    ownerStaffUserId: text(r.ownerStaffUserId),
    partySize: whole(r.partySize),
    dateFrom: text(r.dateFrom),
    dateTo: text(r.dateTo),
    preferredTime: text(r.preferredTime),
    timeZone: text(r.timeZone),
    locationText: text(r.locationText),
    specialRequests: text(r.specialRequests),
    budget: budgetText(r.indicativeBudget),
    details: detailsOf(r.requirementsDetails),
    customerStatusSummary: text(r.customerStatusSummary),
    pendingCustomerQuestion: text(r.pendingCustomerQuestion),
    confirmationEvidenceKind: text(r.confirmationEvidenceKind),
    confirmationEvidenceReference: text(r.confirmationEvidenceReference),
    completionEvidenceKind: text(r.completionEvidenceKind),
    source: text(r.source),
    sourceMessageRef: text(r.sourceMessageRef),
    createdAt: text(r.createdAt),
    submittedAt: text(r.submittedAt),
    stateChangedAt: text(r.stateChangedAt),
  };
}

export type NoteModel = Readonly<{
  id: string;
  kind: string;
  body: string;
  authorStaffUserId: string | null;
  createdAt: string | null;
}>;

export function readNotes(detail: StaffRequestDetail): readonly NoteModel[] {
  return detail.internalNotes
    .map((note, index) => ({
      id: text(note.id) ?? `note-${index}`,
      kind: text(note.kind) ?? "STAFF_NOTE",
      body: text(note.body) ?? "",
      authorStaffUserId: text(note.authorStaffUserId),
      createdAt: text(note.createdAt),
    }))
    .sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""));
}

export type TransitionModel = Readonly<{
  key: string;
  requestVersion: number | null;
  event: string;
  fromState: string | null;
  toState: string | null;
  actorKind: string;
  actorId: string | null;
  evidenceKind: string | null;
  evidenceReference: string | null;
  customerVisibleMessage: string | null;
  internalReason: string | null;
  occurredAt: string | null;
}>;

/** The history, newest first: the page leads with what happened last. */
export function readTransitions(detail: StaffRequestDetail): readonly TransitionModel[] {
  return detail.transitions
    .map((row, index) => ({
      key: `${whole(row.requestVersion) ?? "v"}-${index}`,
      requestVersion: whole(row.requestVersion),
      event: text(row.event) ?? "UNKNOWN",
      fromState: text(row.fromState),
      toState: text(row.toState),
      actorKind: text(row.actorKind) ?? "system",
      actorId: text(row.actorId),
      evidenceKind: text(row.evidenceKind),
      evidenceReference: text(row.evidenceReference),
      customerVisibleMessage: text(row.customerVisibleMessage),
      internalReason: text(row.internalReason),
      occurredAt: text(row.occurredAt),
    }))
    .reverse();
}

// --- what the customer sees --------------------------------------------------------------------------------------------------------

export type CustomerQuestionView = Readonly<{
  sequence: number;
  prompt: string;
  status: string;
  fields: readonly Readonly<{ key: string; label: string; type: string; required: boolean }>[];
  answer: readonly (readonly [string, string])[] | null;
}>;

export type CustomerSeesModel = Readonly<{
  /** The closed status text of the state (contract 7.5). */
  statusText: string;
  /** The staff-set line under it, when there is one. */
  statusLine: string | null;
  /** The open question the customer can answer, if any. */
  openQuestion: CustomerQuestionView | null;
  /** Asked and answered questions, oldest first. */
  questionHistory: readonly CustomerQuestionView[];
  /** The updates staff published for the customer (staff-written text, a CORRECTED marker). Oldest first. */
  updates: readonly CustomerUpdateView[];
  /** The attachments staff SHARED with the customer. A name, a type and a size: never the storage path. */
  attachments: readonly CustomerAttachmentView[];
}>;

export type CustomerUpdateView = Readonly<{
  kind: string;
  text: string;
  isCorrection: boolean;
  createdAt: string;
}>;

export type CustomerAttachmentView = Readonly<{
  displayName: string;
  contentType: string;
  sizeBytes: number;
  sharedAt: string | null;
}>;

function answerPairs(
  question: StaffRequestQuestion,
): readonly (readonly [string, string])[] | null {
  if (question.answer === null) return null;
  return question.fields.length === 0
    ? Object.entries(question.answer).map(([key, value]) => [key, String(value)] as const)
    : question.fields
        .filter((field) => question.answer !== null && field.key in question.answer)
        .map(
          (field) =>
            [field.label, String((question.answer as Record<string, unknown>)[field.key])] as const,
        );
}

function questionView(question: StaffRequestQuestion): CustomerQuestionView {
  return {
    sequence: question.sequence,
    prompt: question.prompt,
    status: question.status,
    fields: question.fields.map((field) => ({
      key: field.key,
      label: field.label,
      type: field.type,
      required: field.required,
    })),
    answer: answerPairs(question),
  };
}

/**
 * The model of the "what the customer sees" block. It takes only: the state (for the closed status text), the customer status line, the
 * prompt, fields and the customer's own answer of each question, the published customer updates and the SHARED attachments (by name). It
 * never reads the notes, the history, the work status, the priority, the owner, the asker, a message ref, a recorded reference, an
 * evidence row, a storage path or an attachment that was not shared. The block renders THIS model and nothing else.
 */
export function buildCustomerSees(
  facts: RequestFacts,
  questions: readonly StaffRequestQuestion[],
  fulfilment: StaffFulfilmentViewResponse | null = null,
): CustomerSeesModel {
  const ordered = [...questions].sort((a, b) => a.sequence - b.sequence);
  const open = ordered.find((question) => question.status === "OPEN") ?? null;
  return {
    statusText: customerRequestWords(facts.state).text,
    statusLine: conciergeNoteOf(facts.customerStatusSummary, facts.state),
    openQuestion: open === null ? null : questionView(open),
    questionHistory: ordered.filter((question) => question.status !== "OPEN").map(questionView),
    // Closed key lists again: of an update only the kind, the text, the correction marker and the time (never the evidence it rests on
    // or the staff member); of an attachment only what the customer reads (never the storage path or who shared it).
    updates: (fulfilment?.customerUpdates ?? []).map((update) => ({
      kind: update.kind,
      text: update.text,
      isCorrection: update.isCorrection,
      createdAt: update.createdAt,
    })),
    attachments: (fulfilment?.attachments ?? [])
      .filter((attachment) => attachment.visibility === "SHARED")
      .map((attachment) => ({
        displayName: attachment.displayName,
        contentType: attachment.contentType,
        sizeBytes: attachment.sizeBytes,
        sharedAt: attachment.sharedAt,
      })),
  };
}
